import { groupDetectionPrompt, propertyMatchingPrompt, scenarioClassificationPrompt, handoffWorkflowSelectionTemplate, actionGroupSelectionTemplate, smsHandoffWorkflowSelectionTemplate, smsActionGroupSelectionTemplate, } from '@super/prompt-sdk';
import { z } from 'zod';
import { EvalPromptType, calcOpenAICostUsd } from '@super/shared-types';
export class ModelEvaluator {
    constructor(aiClient) {
        this.aiClient = aiClient;
    }
    async evaluateCase(promptBuilder, testCase) {
        const startTime = Date.now();
        try {
            const prompt = promptBuilder(testCase.input);
            const actualOutput = await this.aiClient.invoke(prompt);
            const duration = Date.now() - startTime;
            const passed = testCase.expectedOutput
                ? this.compareOutputs(actualOutput, testCase.expectedOutput)
                : true;
            return {
                caseId: testCase.id,
                input: testCase.input,
                actualOutput,
                expectedOutput: testCase.expectedOutput,
                passed,
                duration,
            };
        }
        catch (error) {
            return {
                caseId: testCase.id,
                input: testCase.input,
                actualOutput: '',
                expectedOutput: testCase.expectedOutput,
                passed: false,
                duration: Date.now() - startTime,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    async evaluateSuite(suite) {
        const results = [];
        for (const testCase of suite.cases) {
            const result = await this.evaluateCase(suite.promptBuilder, testCase);
            results.push(result);
        }
        return results;
    }
    compareOutputs(actual, expected) {
        return actual.trim().toLowerCase() === expected.trim().toLowerCase();
    }
    generateReport(results) {
        const totalCases = results.length;
        const passedCases = results.filter(r => r.passed).length;
        const failedCases = totalCases - passedCases;
        const averageDuration = results.reduce((sum, r) => sum + r.duration, 0) / totalCases;
        const passRate = (passedCases / totalCases) * 100;
        return {
            totalCases,
            passedCases,
            failedCases,
            averageDuration,
            passRate,
        };
    }
}
export class PromptEvaluator {
    constructor(openaiClient, template, schema, variableBuilder, outputComparator) {
        this.openaiClient = openaiClient;
        this.template = template;
        this.schema = schema;
        this.variableBuilder = variableBuilder;
        this.outputComparator = outputComparator;
    }
    /**
     * Runs each evalField's extract function against the full LLM response,
     * producing the flat { key: value } map stored in EvalResult.actualOutput.
     */
    extractEvalFields(fullResult) {
        if (!this.template.evalFields?.length)
            return {};
        const extracted = {};
        for (const field of this.template.evalFields) {
            const value = field.extract(fullResult);
            if (value !== undefined) {
                extracted[field.key] = value;
            }
        }
        return extracted;
    }
    async evaluateCase(input) {
        const startTime = Date.now();
        try {
            const variables = this.variableBuilder(input.content, input.inputContext);
            const { result, usage } = await this.openaiClient.executeTemplateStructured(this.template, variables, this.schema);
            const durationMs = Date.now() - startTime;
            const { passed, confidence, details } = this.outputComparator(input.expectedOutput, result);
            const costUsd = calcOpenAICostUsd(this.template.metadata?.model ?? 'unknown', usage.promptTokens, usage.completionTokens);
            return {
                passed,
                actualOutput: this.extractEvalFields(result),
                confidence,
                details,
                promptTokens: usage.promptTokens,
                completionTokens: usage.completionTokens,
                costUsd,
                durationMs,
            };
        }
        catch (error) {
            return {
                passed: false,
                actualOutput: {},
                promptTokens: 0,
                completionTokens: 0,
                costUsd: 0,
                durationMs: Date.now() - startTime,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
}
// ---------------------------------------------------------------------------
// Generic helpers — driven by the template's variableSchema and evalFields
// ---------------------------------------------------------------------------
function unwrapZod(schema) {
    if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
        return unwrapZod(schema.unwrap());
    }
    return schema;
}
/**
 * Serializes a value for substitution into a Handlebars template variable,
 * using the Zod type to determine the correct format:
 *   z.array(z.string())  → numbered list  ("1. Foo\n2. Bar")
 *   z.array(z.object())  → JSON.stringify
 *   everything else      → String()
 */
function serializeForTemplate(zodType, value) {
    const inner = unwrapZod(zodType);
    if (inner instanceof z.ZodArray) {
        const element = unwrapZod(inner.element);
        if (element instanceof z.ZodString) {
            return (value ?? [])
                .map((item, i) => `${i + 1}. ${item}`)
                .join('\n');
        }
        return JSON.stringify(value ?? []);
    }
    return String(value ?? '');
}
/**
 * Builds template variables from a prompt's variableSchema.
 * The contentVariable maps to the `content` parameter; all other variables
 * are read from inputContext and serialized based on their Zod type.
 */
function buildTemplateVariables(template, content, inputContext) {
    if (!template.variableSchema) {
        return { [template.contentVariable ?? 'content']: content };
    }
    const contentVar = template.contentVariable ?? 'content';
    const shape = template.variableSchema.shape;
    const result = {};
    for (const [key, zodType] of Object.entries(shape)) {
        result[key] =
            key === contentVar
                ? content
                : serializeForTemplate(zodType, inputContext[key]);
    }
    return result;
}
/**
 * Evaluates a MatcherSpec against an actual value extracted by an evalField.
 *
 * Scalar semantics (actual is string | number | null):
 *   $exact    — deep equality
 *   $contains — actual string includes this substring
 *   $in       — actual is one of the listed values
 *   $gt/gte/lt/lte — numeric comparisons
 *
 * Array semantics (actual is string[] — evalField type 'text[]'):
 *   $contains — array includes this exact value
 *   $in       — array and the spec set share at least one element
 *   $empty    — true: array has no elements (asserts absence, e.g. no
 *               address extracted for a unit-only mention);
 *               false: array has at least one element
 *
 * Exported so the frontend diff view uses the same semantics.
 */
export function applyMatcher(spec, actual) {
    if (Array.isArray(actual)) {
        const arr = actual;
        if ('$empty' in spec)
            return spec.$empty === false ? arr.length > 0 : arr.length === 0;
        if ('$contains' in spec)
            return arr.some(v => JSON.stringify(v) === JSON.stringify(spec.$contains));
        if ('$in' in spec)
            return spec.$in.some(s => arr.some(v => JSON.stringify(v) === JSON.stringify(s)));
        return false;
    }
    if ('$exact' in spec)
        return JSON.stringify(actual) === JSON.stringify(spec.$exact);
    if ('$contains' in spec)
        return (typeof actual === 'string' && actual.includes(spec.$contains));
    if ('$in' in spec)
        return spec.$in.some(s => JSON.stringify(s) === JSON.stringify(actual));
    if ('$gt' in spec)
        return typeof actual === 'number' && actual > spec.$gt;
    if ('$gte' in spec)
        return typeof actual === 'number' && actual >= spec.$gte;
    if ('$lt' in spec)
        return typeof actual === 'number' && actual < spec.$lt;
    if ('$lte' in spec)
        return typeof actual === 'number' && actual <= spec.$lte;
    return false;
}
/**
 * Runs each evalField's extract function against the full LLM response,
 * then applies the MatcherSpec from expectedOutput. All asserted fields must
 * pass for the case to pass. Unasserted fields (not in expectedOutput) are skipped.
 */
function compareWithEvalFields(evalFields, expected, fullResult) {
    let passed = true;
    for (const field of evalFields) {
        if (field.key in expected) {
            const actualVal = field.extract(fullResult);
            const match = applyMatcher(expected[field.key], actualVal);
            if (!match) {
                passed = false;
                break;
            }
        }
    }
    // Extract confidence and details for result metadata using named fields when present.
    const confField = evalFields.find(f => f.key === 'confidence');
    const confidence = confField
        ? (confField.extract(fullResult) ?? undefined)
        : undefined;
    const detailsField = evalFields.find(f => f.key === 'matchDetails' || f.key === 'reason' || f.key === 'details');
    const details = detailsField
        ? (detailsField.extract(fullResult) ?? undefined)
        : undefined;
    return { passed, confidence, details };
}
// ---------------------------------------------------------------------------
// Generic evaluator factory
// ---------------------------------------------------------------------------
/**
 * Creates a PromptEvaluator for any template that has responseSchema and
 * evalFields defined. Variable building uses variableSchema; output comparison
 * uses each field's explicit extract function plus applyMatcher.
 */
export function createEvaluatorForPrompt(template, openaiClient) {
    if (!template.responseSchema || !template.evalFields?.length) {
        throw new Error(`Template '${template.name}' requires responseSchema and evalFields`);
    }
    const evalFields = template.evalFields;
    return new PromptEvaluator(openaiClient, template, template.responseSchema, (content, inputContext) => buildTemplateVariables(template, content, inputContext), (expected, actual) => compareWithEvalFields(evalFields, expected, actual));
}
// ---------------------------------------------------------------------------
// Prompt registry
// ---------------------------------------------------------------------------
export const EVAL_PROMPT_TEMPLATES = {
    [EvalPromptType.PROPERTY_MATCHING]: propertyMatchingPrompt,
    [EvalPromptType.GROUP_DETECTION]: groupDetectionPrompt,
    [EvalPromptType.SCENARIO_CLASSIFICATION]: scenarioClassificationPrompt,
    [EvalPromptType.HANDOFF_WORKFLOW_SELECTION]: handoffWorkflowSelectionTemplate,
    [EvalPromptType.SMS_HANDOFF_WORKFLOW_SELECTION]: smsHandoffWorkflowSelectionTemplate,
    [EvalPromptType.ACTION_GROUP_SELECTION]: actionGroupSelectionTemplate,
};
/**
 * Full prompt template registry keyed by template name.
 * Covers all templates that may appear in session logs, including those
 * not part of the eval prompt types.
 */
export const PROMPT_TEMPLATE_MAP = {
    [propertyMatchingPrompt.name]: propertyMatchingPrompt,
    [groupDetectionPrompt.name]: groupDetectionPrompt,
    [scenarioClassificationPrompt.name]: scenarioClassificationPrompt,
    [handoffWorkflowSelectionTemplate.name]: handoffWorkflowSelectionTemplate,
    [actionGroupSelectionTemplate.name]: actionGroupSelectionTemplate,
    [smsHandoffWorkflowSelectionTemplate.name]: smsHandoffWorkflowSelectionTemplate,
    [smsActionGroupSelectionTemplate.name]: smsActionGroupSelectionTemplate,
};
// ---------------------------------------------------------------------------
// Convenience factory — kept for backwards compatibility
// ---------------------------------------------------------------------------
export function createPropertyMatchingEvaluator(openaiClient) {
    return createEvaluatorForPrompt(propertyMatchingPrompt, openaiClient);
}
function formatLabel(key, description) {
    if (description)
        return description;
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .trim();
}
function inputFieldType(zodType, isContent) {
    if (isContent)
        return 'textarea';
    const inner = unwrapZod(zodType);
    if (inner instanceof z.ZodNumber)
        return 'number';
    if (inner instanceof z.ZodArray) {
        const element = unwrapZod(inner.element);
        return element instanceof z.ZodString ? 'line-list' : 'json-array';
    }
    return 'textarea';
}
function isRequired(zodType) {
    return (!(zodType instanceof z.ZodOptional) && !(zodType instanceof z.ZodNullable));
}
/**
 * Derives UI field configs from a template's variableSchema (input fields)
 * and evalFields (assertion fields). Input types are inferred from Zod;
 * output types come directly from EvalFieldConfig.type.
 */
export function deriveEvalCaseFields(template) {
    const contentVar = template.contentVariable ?? 'content';
    const inputFields = [];
    const outputFields = [];
    if (template.variableSchema) {
        const shape = template.variableSchema.shape;
        for (const [key, zodType] of Object.entries(shape)) {
            const isContent = key === contentVar;
            const fieldType = inputFieldType(zodType, isContent);
            inputFields.push({
                key,
                target: isContent ? 'content' : 'inputContext',
                label: formatLabel(key, zodType.description),
                type: fieldType,
                required: isContent || isRequired(zodType),
                rows: isContent
                    ? 4
                    : fieldType === 'json-array' || fieldType === 'line-list'
                        ? 5
                        : undefined,
            });
        }
    }
    if (template.evalFields) {
        for (const field of template.evalFields) {
            outputFields.push({
                key: field.key,
                label: formatLabel(field.key),
                type: field.type,
                required: false,
            });
        }
    }
    return { inputFields, outputFields };
}
/**
 * Character-count heuristic for estimating token usage before a run.
 * Actual usage is recorded from the real API response per EvalResult.
 */
export function estimatePropertyMatchTokens(content, inputContext) {
    const estimatedPromptTokens = Math.ceil((content.length + JSON.stringify(inputContext).length) / 4) + 50;
    const estimatedCompletionTokens = 200;
    const estimatedCostUsd = calcOpenAICostUsd(propertyMatchingPrompt.metadata?.model ?? 'unknown', estimatedPromptTokens, estimatedCompletionTokens);
    return { estimatedPromptTokens, estimatedCompletionTokens, estimatedCostUsd };
}
//# sourceMappingURL=evaluator.js.map